import React, { memo, useMemo, useEffect, useCallback } from 'react';
import { 
  MessageSquare, Users, Search, Send, Calendar, XCircle, 
  PhoneCall, Car, DollarSign, Zap, Smartphone, Flame, RefreshCw, X
} from 'lucide-react';
import { applyCustomerFilters } from '../../utils/filterUtils';
import { formatTimestamp, formatConversationTimestamp } from '../../utils/formatters';
import { getInitials, getStatusConfig } from '../../utils/customerUtils';
import MessageInput from './MessageInput';

// Static filter definitions - moved outside component to prevent recreation
const STATUS_FILTERS = [
  { id: 'all', label: 'All', icon: Users, color: '#3b82f6' },
  { id: 'hot-opportunity', label: 'Hot Opportunities', icon: Flame, color: '#ef4444' },
  { id: 'appointment-set', label: 'Appointment Set', icon: Calendar, color: '#10b981' },
  { id: 'offer-sent', label: 'Offer Sent', icon: Send, color: '#f59e0b' },
  { id: 'not-interested', label: 'Lost/Declined', icon: XCircle, color: '#94a3b8' }
];

/**
 * ConversationsTab Component
 * Memoized with prop change logging to debug remounts
 */
const ConversationsTab = ({
  customers,
  selectedCustomer,
  setSelectedCustomer,
  conversations,
  newMessage,
  handleMessageChange,
  handleSendMessage,
  aiResponding,
  customerTyping,
  aiPaused,
  onToggleAI,
  theme,
  colors,
  statusFilter,
  setStatusFilter,
  searchQuery,
  setSearchQuery,
  fetchConversation,
  messagesEndRef,
  isDarkMode,
  tabLoading,
  previousTab
}) => {
  console.log('ConversationsTab component function executed');

  const freshSelectedCustomer = useMemo(() => {
    if (!selectedCustomer) return null;
    return customers.find(c => c.id === selectedCustomer.id) || selectedCustomer;
  }, [customers, selectedCustomer]);

  useEffect(() => {
    console.log('ConversationsTab mounted');
    return () => {
      console.log('ConversationsTab unmounted');
    };
  }, []);
  
  // Memoize filtered customers to avoid recalculating on every render
  const filteredCustomers = useMemo(() => {
    return applyCustomerFilters(customers, { statusFilter, searchQuery });
  }, [customers, statusFilter, searchQuery]);

  // Memoize all style objects that depend on props
  const containerStyle = useMemo(() => ({
    display: 'flex',
    gap: '20px',
    height: 'calc(100vh - 240px)',
    position: 'relative',
    willChange: 'auto',
    contain: 'layout style paint'
  }), []);

  const loadingOverlayStyle = useMemo(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(10, 14, 39, 0.8)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    borderRadius: '16px'
  }), []);

  const loadingContentStyle = useMemo(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    color: colors.primary
  }), [colors.primary]);

  const loadingIconStyle = useMemo(() => ({
    animation: 'spin 1s linear infinite'
  }), []);

  const loadingTextStyle = useMemo(() => ({
    fontSize: '14px',
    fontWeight: '500'
  }), []);

  const sidebarStyle = useMemo(() => ({
    width: '380px',
    position: 'relative',
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 20%, rgba(255, 255, 255, 0.02) 50%, rgba(255, 255, 255, 0.05) 80%, rgba(255, 255, 255, 0.1) 100%)',
    backdropFilter: 'blur(40px) saturate(150%)',
    WebkitBackdropFilter: 'blur(40px) saturate(150%)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '24px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(255, 255, 255, 0.05), 0 0 80px rgba(59, 130, 246, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
  }), []);

  const searchInputContainerStyle = useMemo(() => ({
    padding: '20px',
    borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
  }), []);

  const searchInputWrapperStyle = useMemo(() => ({
    position: 'relative',
    marginBottom: '12px'
  }), []);

  const searchIconStyle = useMemo(() => ({
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'rgba(59, 130, 246, 0.7)'
  }), []);

  const searchInputStyle = useMemo(() => ({
    width: 'calc(100% - 70px)',
    padding: '14px 20px 14px 48px',
    background: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '14px',
    color: theme.text,
    fontSize: '14px',
    outline: 'none',
    transition: 'all 0.3s ease',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
    boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)'
  }), [theme.text]);

  const filterContainerStyle = useMemo(() => ({
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    marginBottom: '12px'
  }), []);

  const customerListStyle = useMemo(() => ({
    flex: 1,
    overflowY: 'auto'
  }), []);

  const emptyStateStyle = useMemo(() => ({
    padding: '40px 20px',
    textAlign: 'center',
    color: theme.textSecondary
  }), [theme.textSecondary]);

  const emptyStateIconStyle = useMemo(() => ({
    opacity: 0.3,
    marginBottom: '16px'
  }), []);

  const conversationPanelStyle = useMemo(() => ({
    flex: 1,
    position: 'relative',
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 20%, rgba(255, 255, 255, 0.02) 50%, rgba(255, 255, 255, 0.05) 80%, rgba(255, 255, 255, 0.1) 100%)',
    backdropFilter: 'blur(40px) saturate(150%)',
    WebkitBackdropFilter: 'blur(40px) saturate(150%)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '24px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(255, 255, 255, 0.05), 0 0 80px rgba(59, 130, 246, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
  }), []);

  const emptyConversationStyle = useMemo(() => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '16px',
    color: theme.textSecondary
  }), [theme.textSecondary]);

  const emptyConversationIconStyle = useMemo(() => ({
    opacity: 0.3
  }), []);

  // Event handlers - use useCallback to prevent recreation
  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, [setSearchQuery]);

  const handleSearchFocus = useCallback((e) => {
    e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
    e.target.style.background = 'rgba(59, 130, 246, 0.05)';
    e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1), 0 0 30px rgba(59, 130, 246, 0.2), inset 0 2px 4px rgba(0, 0, 0, 0.1)';
  }, []);

  const handleSearchBlur = useCallback((e) => {
    e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
    e.target.style.background = 'rgba(255, 255, 255, 0.03)';
    e.target.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.2)';
  }, []);

  const handleFilterClick = useCallback((filterId) => {
    setStatusFilter(filterId);
  }, [setStatusFilter]);

  const handleCustomerClick = useCallback((customer) => {
    setSelectedCustomer(customer);
    fetchConversation(customer.id);
  }, [setSelectedCustomer, fetchConversation]);

  const handleCustomerMouseOver = useCallback((e, customerId) => {
    if (freshSelectedCustomer?.id !== customerId) {
      e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
    }
  }, [freshSelectedCustomer]);

  const handleCustomerMouseOut = useCallback((e, customerId) => {
    if (freshSelectedCustomer?.id !== customerId) {
      e.currentTarget.style.background = 'transparent';
    }
  }, [freshSelectedCustomer]);

  // Memoize filter button styles based on statusFilter
  const getFilterButtonStyle = useCallback((filterId) => ({
    padding: '12px 20px',
    background: statusFilter === filterId 
      ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)' 
      : 'rgba(59, 130, 246, 0.1)',
    border: statusFilter === filterId 
      ? 'none' 
      : '1px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '14px',
    color: statusFilter === filterId ? '#fff' : theme.text,
    fontSize: '13px',
    fontWeight: '600',
    letterSpacing: '0.5px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
    boxShadow: statusFilter === filterId 
      ? '0 0 20px rgba(59, 130, 246, 0.5), 0 4px 15px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)' 
      : 'none',
    overflow: 'hidden',
    position: 'relative'
  }), [statusFilter, theme.text]);

  // Memoize customer row styles
  const getCustomerRowStyle = useCallback((customerId) => ({
    padding: '18px 24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    cursor: 'pointer',
    background: freshSelectedCustomer?.id === customerId 
      ? 'rgba(59, 130, 246, 0.15)' 
      : 'transparent',
    transition: 'all 0.3s ease',
    position: 'relative',
    boxShadow: freshSelectedCustomer?.id === customerId 
      ? '0 0 30px rgba(59, 130, 246, 0.3), inset 0 0 0 1px rgba(59, 130, 246, 0.2)' 
      : 'none'
  }), [freshSelectedCustomer]);

  // Memoize customer row internal styles
  const customerRowContentStyle = useMemo(() => ({
    display: 'flex',
    gap: '12px',
    alignItems: 'start'
  }), []);

  const getCustomerAvatarStyle = useCallback(() => ({
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: `linear-gradient(135deg, ${colors.primary}40 0%, ${colors.secondary}40 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: '700',
    color: colors.primary,
    flexShrink: 0
  }), [colors.primary, colors.secondary]);

  const customerRowDetailsStyle = useMemo(() => ({
    flex: 1,
    minWidth: 0
  }), []);

  const customerRowHeaderStyle = useMemo(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px'
  }), []);

  const customerNameStyle = useMemo(() => ({
    fontSize: '15px',
    fontWeight: '600',
    margin: 0
  }), []);

  const getAIBadgeStyle = useCallback(() => ({
    padding: '2px 6px',
    background: `linear-gradient(135deg, ${colors.primary}30 0%, ${colors.secondary}30 100%)`,
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: '600',
    color: colors.primary
  }), [colors.primary, colors.secondary]);

  const getCustomerVehicleStyle = useCallback(() => ({
    fontSize: '13px',
    color: theme.textSecondary,
    margin: '0 0 4px 0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }), [theme.textSecondary]);

  const customerOfferRowStyle = useMemo(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  }), []);

  const getCustomerOfferStyle = useCallback(() => ({
    fontSize: '14px',
    fontWeight: '600',
    color: colors.primary
  }), [colors.primary]);

  const getCustomerTimestampStyle = useCallback(() => ({
    fontSize: '12px',
    color: theme.textSecondary
  }), [theme.textSecondary]);

  const customerStatusContainerStyle = useMemo(() => ({
    marginTop: '6px'
  }), []);

  // Conversation panel styles
  const conversationHeaderStyle = useMemo(() => ({
    padding: '24px 32px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
    backdropFilter: 'blur(20px)',
    position: 'relative'
  }), []);

  const conversationHeaderContentStyle = useMemo(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }), []);

  const getSelectedCustomerAvatarStyle = useCallback(() => ({
    width: '56px',
    height: '56px',
    borderRadius: '14px',
    background: `linear-gradient(135deg, ${colors.primary}40 0%, ${colors.secondary}40 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: '700',
    color: colors.primary
  }), [colors.primary, colors.secondary]);

  const selectedCustomerDetailsStyle = useMemo(() => ({
    flex: 1
  }), []);

  const selectedCustomerHeaderStyle = useMemo(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px'
  }), []);

  const getSelectedCustomerNameStyle = useCallback(() => ({
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
    color: isDarkMode ? theme.text : '#111827'
  }), [isDarkMode, theme.text]);

  const getAIAutopilotBadgeStyle = useCallback(() => ({
    padding: '4px 10px',
    background: `linear-gradient(135deg, ${colors.primary}30 0%, ${colors.secondary}30 100%)`,
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    color: colors.primary,
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  }), [colors.primary, colors.secondary]);

  const selectedCustomerInfoStyle = useMemo(() => ({
    display: 'flex',
    gap: '16px',
    fontSize: '13px',
    color: isDarkMode ? theme.textSecondary : '#374151'
  }), [isDarkMode, theme.textSecondary]);

  const infoItemStyle = useMemo(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  }), []);

  const getOfferInfoStyle = useCallback(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontWeight: '600',
    color: colors.primary
  }), [colors.primary]);

  const messagesContainerStyle = useMemo(() => ({
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  }), []);

  const conversationStartBannerStyle = useMemo(() => ({
    textAlign: 'center',
    padding: '12px',
    background: 'rgba(59, 130, 246, 0.05)',
    borderRadius: '8px',
    border: '1px solid rgba(59, 130, 246, 0.1)',
    marginBottom: '8px'
  }), []);

  const getConversationStartTextStyle = useCallback(() => ({
    fontSize: '12px',
    color: theme.textSecondary,
    fontWeight: '500'
  }), [theme.textSecondary]);

  const conversationStartTimestampStyle = useMemo(() => ({
    fontSize: '11px',
    color: '#6b7280',
    marginTop: '4px'
  }), []);

  const smartphoneIconStyle = useMemo(() => ({
    display: 'inline-block',
    marginRight: '6px',
    verticalAlign: 'middle'
  }), []);

  const getSystemMessageContainerStyle = useCallback(() => ({
    display: 'flex',
    justifyContent: 'center',
    margin: '12px 0'
  }), []);

  const getSystemMessageStyle = useCallback(() => ({
    padding: '8px 16px',
    borderRadius: '12px',
    background: 'rgba(59, 130, 246, 0.1)',
    border: '1px solid rgba(59, 130, 246, 0.2)',
    color: colors.primary,
    fontSize: '12px',
    fontWeight: '600',
    textAlign: 'center'
  }), [colors.primary]);

  const customerTypingContainerStyle = useMemo(() => ({
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: '12px'
  }), []);

  const customerTypingBubbleStyle = useMemo(() => ({
    padding: '12px 16px',
    borderRadius: '16px',
    background: '#E5E7EB',
    color: '#1F2937'
  }), []);

  const getMessageContainerStyle = useCallback((isCustomer) => ({
    display: 'flex',
    justifyContent: isCustomer ? 'flex-start' : 'flex-end'
  }), []);

  const getMessageBubbleStyle = useCallback((isCustomer) => ({
    maxWidth: '70%',
    padding: '14px 18px',
    borderRadius: isCustomer ? '4px 20px 20px 20px' : '20px 4px 20px 20px',
    background: isCustomer 
      ? 'rgba(255, 255, 255, 0.05)'
      : 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(37, 99, 235, 0.2) 100%)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: isCustomer 
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid rgba(59, 130, 246, 0.3)',
    color: '#fff',
    fontSize: '14px',
    lineHeight: '1.6',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
    boxShadow: isCustomer 
      ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1)' 
      : '0 0 30px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
    wordWrap: 'break-word',
    animation: 'fadeInUp 0.3s ease-out'
  }), [isDarkMode, colors.primary, colors.secondary]);

  const messageTextStyle = useMemo(() => ({
    margin: 0
  }), []);

  const getMessageTimestampStyle = useCallback((isCustomer) => ({
    fontSize: '11px',
    opacity: 0.7,
    marginTop: '4px',
    textAlign: isCustomer ? 'left' : 'right'
  }), []);

  const typingIndicatorContainerStyle = useMemo(() => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '8px',
    animation: 'fadeIn 0.3s ease-out'
  }), []);

  const getTypingIndicatorStyle = useCallback(() => ({
    padding: '12px 16px',
    borderRadius: '18px 4px 18px 18px',
    background: 'rgba(59, 130, 246, 0.2)',
    color: '#fff',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 0 20px rgba(59, 130, 246, 0.2)',
    minWidth: '120px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
  }), []);

  const typingTextStyle = useMemo(() => ({
    fontSize: '13px',
    fontWeight: '500'
  }), []);

  const typingDotsStyle = useMemo(() => ({
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
    paddingTop: '2px'
  }), []);

  return (
    <div 
      className={`tab-content ${tabLoading ? 'tab-fade-out' : 'tab-fade-in'}`} 
      style={containerStyle}
    >
      {tabLoading && previousTab && (
        <>
          <div className="tab-loading-bar" />
          <div className="tab-loading-overlay" style={loadingOverlayStyle}>
            <div style={loadingContentStyle}>
              <RefreshCw size={24} style={loadingIconStyle} />
              <span style={loadingTextStyle}>Loading...</span>
            </div>
          </div>
        </>
      )}
      <div style={sidebarStyle}>
        <div style={searchInputContainerStyle}>
          <div style={searchInputWrapperStyle}>
            <Search size={18} style={searchIconStyle} />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={searchInputStyle}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
            />
          </div>

          <div style={filterContainerStyle}>
            {STATUS_FILTERS.map(filter => {
              const FilterIcon = filter.icon;
              return (
                <button
                  key={filter.id}
                  onClick={() => handleFilterClick(filter.id)}
                  style={getFilterButtonStyle(filter.id)}
                >
                  <FilterIcon size={18} />
                  {filter.label}
                </button>
              );
            })}
          </div>

        </div>

        <div style={customerListStyle}>
          {filteredCustomers.length === 0 ? (
            <div style={emptyStateStyle}>
              <Users size={48} style={emptyStateIconStyle} />
              <p>No customers found</p>
            </div>
          ) : (
            filteredCustomers.map(customer => (
              <div
                key={customer.id}
                onClick={() => handleCustomerClick(customer)}
                style={getCustomerRowStyle(customer.id)}
                onMouseOver={(e) => handleCustomerMouseOver(e, customer.id)}
                onMouseOut={(e) => handleCustomerMouseOut(e, customer.id)}
              >
                <div style={customerRowContentStyle}>
                  <div style={getCustomerAvatarStyle()}>
                    {getInitials(customer.name)}
                  </div>
                  <div style={customerRowDetailsStyle}>
                    <div style={customerRowHeaderStyle}>
                      <h4 style={customerNameStyle}>
                        {customer.name}
                      </h4>
                      {customer.aiAutopilot && (
                        <span style={getAIBadgeStyle()}>
                          AI
                        </span>
                      )}
                    </div>
                    <p style={getCustomerVehicleStyle()}>
                      {customer.vehicle}
                    </p>
                    <div style={customerOfferRowStyle}>
                      <span style={getCustomerOfferStyle()}>
                        {customer.currentOffer}
                      </span>
                      <span style={getCustomerTimestampStyle()}>
                        {customer.lastMessage ? formatTimestamp(customer.lastMessage.timestamp) : 'No messages'}
                      </span>
                    </div>
                    <div style={customerStatusContainerStyle}>
                      {(() => {
                        const config = getStatusConfig(customer.acquisitionStatus);
                        return (
                          <span style={{
                            padding: '4px 8px',
                            background: config.bg,
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: config.color,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <config.icon size={16} /> {config.label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div style={conversationPanelStyle}>
        {freshSelectedCustomer ? (
          <>
            <div style={conversationHeaderStyle}>
              <div style={conversationHeaderContentStyle}>
                <div style={getSelectedCustomerAvatarStyle()}>
                  {getInitials(freshSelectedCustomer.name)}
                </div>
                <div style={selectedCustomerDetailsStyle}>
                  <div style={selectedCustomerHeaderStyle}>
                    <h3 style={getSelectedCustomerNameStyle()}>
                      {freshSelectedCustomer.name}
                    </h3>
                    {freshSelectedCustomer.aiAutopilot && (
                      <span style={getAIAutopilotBadgeStyle()}>
                        <Zap size={12} /> AI Autopilot 24/7
                      </span>
                    )}
                  </div>
                  <div style={selectedCustomerInfoStyle}>
                    <span style={infoItemStyle}>
                      <PhoneCall size={14} /> {freshSelectedCustomer.phone}
                    </span>
                    <span style={infoItemStyle}>
                      <Car size={14} /> {freshSelectedCustomer.vehicle}
                    </span>
                    <span style={getOfferInfoStyle()}>
                      <DollarSign size={14} /> {freshSelectedCustomer.currentOffer}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {(() => {
                    const config = getStatusConfig(freshSelectedCustomer.acquisitionStatus);
                    return (
                      <span style={{
                        padding: '8px 16px',
                        background: config.bg,
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: config.color,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <config.icon size={16} /> {config.label}
                      </span>
                    );
                  })()}
                  <button
                    type="button"
                    onClick={() => onToggleAI(freshSelectedCustomer.id, !aiPaused)}
                    style={{
                      padding: '8px 16px',
                      background: aiPaused 
                        ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`
                        : 'rgba(255, 255, 255, 0.1)',
                      border: aiPaused ? 'none' : '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s',
                      boxShadow: aiPaused ? `0 8px 24px ${colors.primary}40` : 'none'
                    }}
                  >
                    {aiPaused ? (
                      <>
                        <Zap size={14} /> Resume AI
                      </>
                    ) : (
                      <>
                        <X size={14} /> Pause AI
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div style={messagesContainerStyle}>
              <div style={conversationStartBannerStyle}>
                <p style={getConversationStartTextStyle()}>
                  <Smartphone size={16} style={smartphoneIconStyle} /> Beginning of text message conversation
                </p>
                <p style={conversationStartTimestampStyle}>
                  {conversations[freshSelectedCustomer.id]?.[0] && formatConversationTimestamp(conversations[freshSelectedCustomer.id][0].timestamp)}
                </p>
              </div>
              {(conversations[freshSelectedCustomer.id] || []).map((message, index) => {
                // Handle system messages separately
                if (message.role === 'system' || message.sender === 'system') {
                  return (
                    <div key={`system-${message.timestamp || index}-${freshSelectedCustomer.id}`} style={getSystemMessageContainerStyle()}>
                      <div style={getSystemMessageStyle()}>
                        {message.content || message.text || message.message || 'System notification'}
                      </div>
                    </div>
                  );
                }
                
                const senderValue = message.sender || message.role;
                const isCustomer = senderValue === 'customer';
                const isUser = senderValue === 'user';
                const isAI = senderValue === 'ai' || senderValue === 'assistant';
                const messageContent = message.content || message.text || message.message || '';
                
                return (
                  <div
                    key={`message-${message.timestamp || index}-${freshSelectedCustomer.id}-${message.sender || message.role}`}
                    style={{
                      display: 'flex',
                      justifyContent: isCustomer ? 'flex-start' : 'flex-end',
                      marginBottom: '12px'
                    }}
                  >
                    <div
                      style={{
                        maxWidth: '70%',
                        padding: '12px 16px',
                        borderRadius: '16px',
                        background: isUser
                          ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                          : isCustomer 
                            ? '#E5E7EB'
                            : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                        color: isCustomer ? '#1F2937' : 'white',
                        borderBottomLeftRadius: isCustomer ? '4px' : '16px',
                        borderBottomRightRadius: isCustomer ? '16px' : '4px',
                        boxShadow: isCustomer
                          ? '0 2px 8px rgba(0, 0, 0, 0.2)'
                          : `0 2px 12px ${isUser ? '#10b98140' : `${colors.primary}40`}`
                      }}
                    >
                      <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
                        {messageContent}
                      </p>
                      <p style={{ 
                        margin: '4px 0 0 0', 
                        fontSize: '11px', 
                        opacity: 0.7 
                      }}>
                        {formatTimestamp(message.timestamp)}
                      </p>
                    </div>
                  </div>
                );
              })}
              
              {customerTyping && (
                <div style={customerTypingContainerStyle}>
                  <div style={customerTypingBubbleStyle}>
                    <div style={typingDotsStyle}>
                      <span className="typing-dot" />
                      <span className="typing-dot" />
                      <span className="typing-dot" />
                    </div>
                  </div>
                </div>
              )}

              {aiPaused ? (
                <div style={{ 
                  padding: '8px 12px', 
                  background: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: colors.primary,
                  marginBottom: '8px'
                }}>
                  ⏸️ AI Paused - You have manual control. Messages you send will keep AI paused.
                </div>
              ) : (
                <div style={{ 
                  padding: '8px 12px', 
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  fontSize: '11px',
                  color: theme.textSecondary,
                  marginBottom: '8px'
                }}>
                  💡 Manual messages pause AI autopilot for this conversation
                </div>
              )}

              {/* Typing Indicator */}
              {aiResponding && (
                <div
                  className="typing-indicator-container"
                  style={typingIndicatorContainerStyle}
                >
                  <div style={getTypingIndicatorStyle()}>
                    <span style={typingTextStyle}>AI is typing</span>
                    <div style={typingDotsStyle}>
                      <span className="typing-dot typing-dot-1" />
                      <span className="typing-dot typing-dot-2" />
                      <span className="typing-dot typing-dot-3" />
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            <MessageInput
              value={newMessage}
              onChange={handleMessageChange}
              onSend={handleSendMessage}
              disabled={aiResponding || !freshSelectedCustomer}
              theme={theme}
              colors={colors}
            />
          </>
        ) : (
          <div style={emptyConversationStyle}>
            <MessageSquare size={64} style={emptyConversationIconStyle} />
            <p style={{ fontSize: '16px', fontWeight: '500' }}>Select a customer to view conversation</p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes typingPulse {
          0%, 60%, 100% {
            opacity: 0.3;
            transform: translateY(0) scale(0.8);
          }
          30% {
            opacity: 1;
            transform: translateY(-4px) scale(1);
          }
        }
        .typing-dot {
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.9);
          animation: typingPulse 1.4s infinite ease-in-out;
        }
        .typing-dot-1 { animation-delay: 0s; }
        .typing-dot-2 { animation-delay: 0.2s; }
        .typing-dot-3 { animation-delay: 0.4s; }
        .typing-indicator-container { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </div>
  );
};

ConversationsTab.displayName = 'ConversationsTab';

// Memoize with default shallow comparison to prevent unnecessary re-renders
export default memo(ConversationsTab);

